import { Box, Button, Step, StepLabel, Stepper } from "@mui/material";
import { BackdropOpen } from "App";
import { Methods } from "enums/Methods";
import {
  useGetArrayMethodLabwareTypesLazyQuery,
  useGetExistingMethodReservationLazyQuery,
  useGetExperimentSettingsLazyQuery,
  useGetPlateBarcodePreviewMutation,
  useReserveMethodMutation,
} from "graphql/generated/schema-types";
import { GetUrl } from "helpers/get-url";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import React, { useEffect, useState } from "react";
import { ConfirmDialog } from "shared-components/ModalsAndDialogs/ConfirmDialog";
import { Alert, AlertType } from "shared-components/toast";
import styled from "styled-components";
import { NewWorklistPreview } from "../shared/components/NewWorklistPreview";
import {
  ExternalThawStep,
  LabwareTypeInfo,
  Worklist,
} from "../shared/interfaces";
import {
  extractPlateCode,
  getDeadPlateTypeInfo,
  getDestPlateTypeInfo,
  getIntPlateTypeInfo,
  getPlateCode,
  getSourcePlateTypeInfo,
  incrementDestBarcode,
} from "../shared/WorklistHelpers";
import { DestinationPlatesContainer } from "./plates/DestinationPlatesContainer";
import { IntermediatePlatesContainer } from "./plates/IntPlatesContainer";
import { SelectableIntPlatesContainer } from "./plates/SelectableIntPlatesContainer";
import { SourcePlatesContainer } from "./plates/SourcePlatesContainer";
import { ExternalThawSidebar } from "./sidebar/ExternalThawSideBar";
import { ExternalThawToolActions } from "./state";
import { ExternalThawInternalAction } from "./state/actions";
import { useSetWorkistPreviewOpen, useSetWorklist } from "./state/handlers";
import { useExternalThawStateProvider } from "./state/StateProvider";

const StyledPlateDiv = styled.div`
  height: calc(100vh - 200px);
  display: grid;
  grid-template: auto / 1fr 2fr;
`;

const steps = [
  "Select Source To Intermediate Mapping",
  "Select Intermediate To Destination Mapping",
];

export const ExternalThaw = () => {
  const { externalThawInternalState, externalThawDispatch } =
    useExternalThawStateProvider();
  const methodSettings = externalThawInternalState.methodSettings;
  const dispatch = useAppReduxDispatch();
  const [sourcePlateTypes, setSourcePlateTypes] = useState<LabwareTypeInfo[]>();
  const [destPlateTypes, setDestPlateTypes] = useState<LabwareTypeInfo[]>();
  const [intPlateTypes, setIntPlatesTypes] = useState<LabwareTypeInfo[]>();
  const [deadPlateTypes, setDeadPlatesTypes] = useState<LabwareTypeInfo[]>();
  const externalThawReduxState = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present
  );
  const sourcePlateInfo = externalThawReduxState.sourcePlateInfo;
  const destPlateInfo = externalThawReduxState.destPlateInfo;
  const intPlateInfo = externalThawReduxState.intPlateInfo;
  const deadPlateBarcode = externalThawReduxState.deadPlateBarcode;
  const deadPlateType = externalThawReduxState.deadPlateType;

  const [getAcceptablePlateTypes, { data: acceptablePlateTypes }] =
    useGetArrayMethodLabwareTypesLazyQuery({
      onCompleted(acceptablePlateTypes) {
        if (!acceptablePlateTypes) return;
        setDeadPlatesTypes(getDeadPlateTypeInfo(acceptablePlateTypes));
      },
      fetchPolicy: "network-only",
    });
  const [getPlateBarcodePreview] = useGetPlateBarcodePreviewMutation();
  const [reserveMethod, { loading }] = useReserveMethodMutation();
  const setWorklist = useSetWorklist();
  const setWorklistPreviewOpen = useSetWorkistPreviewOpen();
  useEffect(() => {
    if (
      externalThawInternalState.methodSettings.arraySystemAutomationMethodId
    ) {
      getAcceptablePlateTypes({
        variables: {
          where: {
            arraySystemAutomationMethodId: {
              eq: externalThawInternalState.methodSettings
                .arraySystemAutomationMethodId,
            },
          },
        },
      });
    }
  }, [externalThawInternalState.methodSettings.arraySystemAutomationMethodId]);

  useEffect(() => {
    if (acceptablePlateTypes) {
      setSourcePlateTypes(getSourcePlateTypeInfo(acceptablePlateTypes));
      setDestPlateTypes(getDestPlateTypeInfo(acceptablePlateTypes));
      setIntPlatesTypes(getIntPlateTypeInfo(acceptablePlateTypes));
      setDeadPlatesTypes(getDeadPlateTypeInfo(acceptablePlateTypes));
    }
  }, [acceptablePlateTypes]);

  useEffect(() => {
    const templateHasBeenLoaded =
      sourcePlateInfo[0].plateBarcode !== "" &&
      destPlateInfo.some(
        (e) => e.labwareTypeCode !== "" && e.plateBarcode === ""
      );

    if (!templateHasBeenLoaded) return;

    const generateTemplateDestPlateBarcodes = async () => {
      const usedDestPlates = destPlateInfo.filter(
        (e) => e.labwareTypeCode !== ""
      );

      const sourcePlateBarcodes = sourcePlateInfo
        .filter((sp) => sp.plateBarcode !== "")
        .map((sp) => sp.plateBarcode);

      const plateCodeToLastIncrementedBarcode = new Map<string, string>(); // {"C96W" => "BREPR_C96W_102"}
      const usedPlateCodes = new Set<string>();
      const barcodes: string[] = [];

      for (const destPlate of usedDestPlates) {
        const plateCode = getPlateCode(destPlate.labwareTypeCode);

        let nextDestPlateBarcode = "";

        //if we've already queried using platecode, just increment
        if (usedPlateCodes.has(plateCode)) {
          const lastIncrementedBarcode =
            plateCodeToLastIncrementedBarcode.get(plateCode)!;
          nextDestPlateBarcode = incrementDestBarcode(lastIncrementedBarcode);
        } else {
          //if not, get barcode with new platecode
          const res = await getPlateBarcodePreview({
            variables: {
              methodId: Methods.External_Thaw,
              sourcePlateBarcodes,
              destPlateCode: plateCode,
            },
          });

          nextDestPlateBarcode =
            res?.data?.plateBarcodePreview?.find((p) => p?.plateType === "Dest")
              ?.plateBarcode ?? "";
        }

        usedPlateCodes.add(plateCode);
        plateCodeToLastIncrementedBarcode.set(plateCode, nextDestPlateBarcode);
        barcodes.push(nextDestPlateBarcode);
      }

      for (let i = 0; i < usedDestPlates.length; i++) {
        dispatch(
          ExternalThawToolActions.UPDATE_DESTINATION_PLATE_INFO({
            index: usedDestPlates[i].index,
            key: "plateBarcode",
            value: barcodes[i],
          })
        );
      }
    };

    generateTemplateDestPlateBarcodes();
  }, [sourcePlateInfo[0].plateBarcode]);

  useEffect(() => {
    const uniqueDestPlateTypes = [
      ...new Set(
        destPlateInfo
          .map((e) => e.labwareTypeCode)
          .filter((labware) => labware !== "")
      ),
    ];
    if (
      uniqueDestPlateTypes.length > 0 &&
      sourcePlateInfo[0].plateBarcode !== "" &&
      destPlateInfo[0].labwareTypeCode !== ""
    ) {
      const sourcePlateBarcodes = sourcePlateInfo
        .filter((sourcePlate) => sourcePlate.plateBarcode !== "")
        .map((sourcePlate) => sourcePlate.plateBarcode);

      getPlateBarcodePreview({
        variables: {
          methodId: Methods.External_Thaw,
          sourcePlateBarcodes: sourcePlateBarcodes,
          destPlateCode:
            uniqueDestPlateTypes.length > 1
              ? "Y"
              : extractPlateCode(destPlateInfo[0].plateBarcode) ??
                getPlateCode(destPlateInfo[0].labwareTypeCode),
        },
      }).then((res) => {
        if (externalThawInternalState.uploadedWorklist) {
          //After worklist is uploaded set state variable to false
          externalThawDispatch({
            type: ExternalThawInternalAction.SET_STATE,
            payload: {
              key: "uploadedWorklist",
              value: false,
            },
          });
          return;
        }

        const plates = res!.data!.plateBarcodePreview!;

        const intPlateBarcode = plates.find(
          (plate) => plate?.plateType === "Int"
        )?.plateBarcode;
        const deadPlateBarcode = plates.find(
          (plate) => plate?.plateType === "Dead"
        )?.plateBarcode;

        dispatch(
          ExternalThawToolActions.UPDATE_AUX_PLATEBARCODES({
            intPlateBarcode: intPlateBarcode,
            deadPlateBarcode: deadPlateBarcode,
          })
        );
      });
    }
  }, [sourcePlateInfo, destPlateInfo, deadPlateType]);

  const [getExistingMethodReservation, { data: methodReservation }] =
    useGetExistingMethodReservationLazyQuery({
      fetchPolicy: "network-only",
    });

  const [getExpSettings] = useGetExperimentSettingsLazyQuery({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const expSettingsRow = data?.experimentSettings?.[0];
      if (!expSettingsRow) return;
      externalThawDispatch({
        type: ExternalThawInternalAction.SET_EXPERIMENT_SETTINGS,
        payload: { experimentSettings: expSettingsRow.experimentSetting },
      });
    },
    onError: (err) => {
      Alert({
        type: AlertType.ERROR,
        message: `Experiment Settings Error: ${err}`,
      });
    },
  });

  useEffect(() => {
    const intWells = externalThawReduxState.worklistValues.harvestWells.length;
    const numAliquots = methodSettings.numberOfAliquots;
    if (!intWells || !numAliquots) return;

    const intPlateCols = Math.ceil(intWells / 8);
    const AUTO_LIVEDEAD_COLS_ANALYZED = 12;
    let deadTotalCols = intPlateCols * numAliquots;
    deadTotalCols =
      deadTotalCols > 8 ? AUTO_LIVEDEAD_COLS_ANALYZED : deadTotalCols; //Auto_LiveDead covers 9-12

    getExpSettings({
      variables: {
        where: {
          and: [
            { maxColumnsAnalyzed: { eq: deadTotalCols } },
            { experimentSettingScanTypeId: { eq: 8 } }, // Dead + Total
          ],
        },
      },
    });
  }, [
    externalThawReduxState.worklistValues.harvestWells,
    methodSettings.numberOfAliquots,
    getExpSettings,
  ]);

  useEffect(() => {
    BackdropOpen({
      open: loading,
      header: "Generating Worklist",
      subText: "Do not reload page",
      size: 60,
    });
  }, [loading]);

  const handleWorklistPreviewClose = (
    generateWorklist: boolean,
    worklist: Worklist[],
    reservationTime: Date
  ) => {
    const methodReservationInput = {
      methodId: Methods.External_Thaw,
      selectedSystem: methodSettings.selectedSystem,
      sourcePlateInfo: sourcePlateInfo.map((plate) => ({
        plateBarcode: plate.plateBarcode,
        plateType: plate.labwareTypeCode,
      })),
      destPlateInfo: destPlateInfo.map((plate: any) => ({
        plateBarcode: plate.plateBarcode,
        plateType: plate.labwareTypeCode,
      })),
      intPlateBarcode: intPlateInfo[0].plateBarcode,
      deadPlateBarcode: deadPlateBarcode,
      worklist: worklist,
      reservationTime,
      arraySystemAutomationMethodId:
        externalThawInternalState.methodSettings.arraySystemAutomationMethodId,
    };
    if (generateWorklist) {
      reserveMethod({
        variables: {
          methodReservationInput: methodReservationInput,
        },
      }).then((res) => {
        // Can't download files via GraphQL
        window.location.href = `${GetUrl()}/api/Download/DownloadWorklist?methodReservationID=${
          res.data?.reserveMethod?.methodReservationId
        }`;
        getExistingMethodReservation({
          variables: {
            where: {
              isCompleted: { eq: false },
              methodReservationsPlateBarcodes: {
                some: { plateBarcode: { eq: sourcePlateInfo[0].plateBarcode } },
              },
            },
          },
        });
      });
    }
    setWorklistPreviewOpen(false);
  };

  const handleNext = async () => {
    externalThawDispatch({
      type: ExternalThawInternalAction.SET_NEXT_STEP,
    });
  };

  const handleBack = () => {
    externalThawDispatch({
      type: ExternalThawInternalAction.SET_PREV_STEP,
    });
  };

  useEffect(() => {
    const intWellVol = getIntWellVol();
    dispatch(
      ExternalThawToolActions.UPDATE_INT_PLATE_INFO({
        index: 0,
        key: "intWellVol",
        value: intWellVol,
      })
    );
  }, [
    intPlateInfo[0].resuspensionVol,
    externalThawInternalState.methodSettings.numberOfAliquots,
    externalThawInternalState.methodSettings.aliquotVolume,
  ]);

  const getIntWellVol = () => {
    const intWellVol =
      intPlateInfo[0].resuspensionVol -
      externalThawInternalState.methodSettings.numberOfAliquots *
        externalThawInternalState.methodSettings.aliquotVolume;
    return intWellVol;
  };

  return (
    <React.Fragment>
      <StyledPlateDiv>
        <ExternalThawSidebar deadPlateInfo={deadPlateTypes} />
        <Box sx={{ width: "95%", height: "100%" }}>
          <Stepper
            activeStep={externalThawInternalState.step}
            sx={{ width: "75%", margin: "0 auto" }}
          >
            {steps.map((label) => {
              const stepProps: { completed?: boolean } = {};
              const labelProps: {
                optional?: React.ReactNode;
              } = {};
              return (
                <Step key={label} {...stepProps}>
                  <StepLabel {...labelProps}>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
          <div>
            <div style={{ display: "flex" }}>
              {externalThawInternalState.step ===
              ExternalThawStep.SelectSourceToIntermediate ? (
                <React.Fragment>
                  <SourcePlatesContainer sourcePlateInfo={sourcePlateTypes} />
                  <IntermediatePlatesContainer intPlateInfo={intPlateTypes} />
                </React.Fragment>
              ) : externalThawInternalState.step ===
                ExternalThawStep.SelectIntermediateToDestination ? (
                <React.Fragment>
                  <SelectableIntPlatesContainer intPlateInfo={intPlateTypes} />
                  <DestinationPlatesContainer destPlateInfo={destPlateTypes} />
                </React.Fragment>
              ) : null}
            </div>
            <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
              <Button
                color="inherit"
                disabled={externalThawInternalState.step === 0}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />
              {externalThawInternalState.step < steps.length - 1 ? (
                <Button onClick={handleNext}>Next</Button>
              ) : null}
            </Box>
          </div>
        </Box>
      </StyledPlateDiv>
      <NewWorklistPreview
        isOpen={externalThawInternalState.worklistPreviewOpen}
        worklist={externalThawInternalState.worklist}
        setWorklist={setWorklist}
        handleClose={handleWorklistPreviewClose}
      />

      <ConfirmDialog leftButtonText="No" rightButtonText="Yes" />
    </React.Fragment>
  );
};
